import { Preferences } from '@capacitor/preferences';
import type { UIMessage } from 'ai';

const STORAGE_PREFIX = 'ai-chat-history:';

export interface AiChatHistorySnapshot {
  messages: UIMessage[];
  updatedAt: number;
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

export async function loadAiChatHistory(
  userId: string,
): Promise<AiChatHistorySnapshot | null> {
  if (!userId) {
    return null;
  }

  try {
    const { value } = await Preferences.get({ key: storageKey(userId) });
    if (!value) {
      return null;
    }

    const parsed = JSON.parse(value) as Partial<AiChatHistorySnapshot>;
    if (!Array.isArray(parsed.messages) || parsed.messages.length === 0) {
      return null;
    }

    return {
      messages: parsed.messages as UIMessage[],
      updatedAt:
        typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export async function saveAiChatHistory(
  userId: string,
  messages: UIMessage[],
): Promise<void> {
  if (!userId || messages.length === 0) {
    return;
  }

  const snapshot: AiChatHistorySnapshot = {
    messages,
    updatedAt: Date.now(),
  };

  await Preferences.set({
    key: storageKey(userId),
    value: JSON.stringify(snapshot),
  });
}

export async function clearAiChatHistory(userId: string): Promise<void> {
  if (!userId) {
    return;
  }

  await Preferences.remove({ key: storageKey(userId) });
}
